import React from 'react';
import { Shield, Award, Users, Car } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import SectionHeader from './SectionHeader';
import CountUp from './CountUp';

const About = () => {
  const features = [
    {
      icon: Car,
      title: 'Premium Selection',
      description: "Curated collection of the world's finest luxury vehicles from top manufacturers.",
      accent: 'from-[#3DED97]/20 to-transparent',
    },
    {
      icon: Shield,
      title: 'Quality Guarantee',
      description: 'Every vehicle undergoes rigorous inspection and comes with comprehensive warranty coverage.',
      accent: 'from-[#B0FC38]/20 to-transparent',
    },
    {
      icon: Award,
      title: 'Expert Service',
      description: 'Professional team with years of experience in luxury automotive sales and service.',
      accent: 'from-[#3DED97]/20 to-transparent',
    },
    {
      icon: Users,
      title: 'Customer Satisfaction',
      description: 'Dedicated to providing exceptional customer experience and after-sales support.',
      accent: 'from-[#B0FC38]/20 to-transparent',
    },
  ];

  const stats = [
    { value: '500+', label: 'Luxury Vehicles Sold' },
    { value: '98%', label: 'Customer Satisfaction' },
    { value: '10+', label: 'Years of Excellence' },
  ];

  return (
    <section id="about" className="relative py-28 bg-gradient-to-b from-black via-gray-950 to-gray-900 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#3DED97]/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          badge="Who We Are"
          title="About"
          highlight="SharpCars"
          description="We are the premier destination for luxury automotive excellence in Abuja — bringing you the finest selection of vehicles that combine performance, style, and sophistication."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className="group premium-card glass-card p-8 h-full text-center relative overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-b ${feature.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className="relative z-10">
                    <div className="inline-flex p-4 rounded-2xl bg-[#3DED97]/10 mb-6 group-hover:scale-110 group-hover:bg-[#3DED97]/20 transition-all duration-500">
                      <Icon className="h-8 w-8 text-[#3DED97]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {stats.map((stat, index) => (
            <AnimateOnScroll key={index} animation="scale-up" delay={index * 120}>
              <div className="text-center p-10 rounded-2xl glass-card premium-card">
                <div className="font-display text-5xl font-bold gradient-text mb-3">
                  <CountUp value={stat.value} />
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll animation="fade-up" delay={150}>
          <div className="mb-20 flex flex-col items-center">
            <span className="section-badge mb-6 inline-block">Leadership</span>
            <div className="relative max-w-xs sm:max-w-sm w-full rounded-3xl overflow-hidden border border-[#3DED97]/15 premium-card glass-card shadow-2xl shadow-[#3DED97]/5">
              <img
                src="/images/ceo.jpeg"
                alt="SharpCars CEO"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll animation="fade-up" delay={200}>
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#3DED97]/10 via-[#B0FC38]/5 to-[#3DED97]/10" />
            <div className="relative p-10 sm:p-14 text-center border border-[#3DED97]/15 rounded-3xl">
              <span className="section-badge mb-6 inline-block">Our Mission</span>
              <p className="font-display text-xl sm:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed italic">
                &ldquo;To provide discerning customers with access to the world&apos;s most prestigious luxury vehicles,
                ensuring uncompromising quality, exceptional service, and an unparalleled automotive experience.&rdquo;
              </p>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default About;
